import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export interface TableCellDisplayProps {
  value: string | number | null;
  isEmpty?: boolean;
  hasError?: boolean;
  /** i18n key for error text (e.g. 'general.required_price'). Pass the key, not t(key). */
  errorMessage?: string;
  className?: string;
  /** Value for data-user-guide attribute (user tour / product tutorials) */
  dataUserGuide?: string;
  tooltipContent?: React.ReactNode;
  children?: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  /** Whether the cell is editable (affects cursor) */
  isEditable?: boolean;
  /** Whether this is a parent row (affects cursor/opacity) */
  isParent?: boolean;
  /** Click handler to start editing */
  onClick?: ((e: React.MouseEvent<HTMLDivElement>) => void) | (() => void);
  /** Whether this field was recently modified (for highlighting) */
  isRecentlyModified?: boolean;
}

/**
 * Reusable display component for table cells
 * Handles error styling, empty states, and tooltips
 */
export const TableCellDisplay: React.FC<TableCellDisplayProps> = ({
  value,
  isEmpty = false,
  hasError = false,
  errorMessage,
  className,
  dataUserGuide,
  tooltipContent,
  children,
  align = 'left',
  isEditable = true,
  isParent = false,
  onClick,
  isRecentlyModified = false,
}) => {
  const { t } = useTranslation();

  const interactiveClasses = isParent
    ? 'cursor-pointer opacity-50'
    : isEditable
      ? 'cursor-pointer'
      : 'cursor-not-allowed opacity-50';

  const displayClasses = cn(
    'flex h-full w-full items-center rounded-none px-2 text-sm',
    align === 'right' && 'justify-end',
    align === 'center' && 'justify-center',
    hasError
      ? 'bg-red-50 border-l-2 border-red-500'
      : isRecentlyModified
        ? 'bg-blue-50 border-l-2 border-blue-500'
        : 'bg-transparent',
    interactiveClasses,
    className
  );

  const content = (
    <div
      className={displayClasses}
      data-user-guide={dataUserGuide}
      onClick={onClick}
    >
      {isEmpty ? (
        <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-muted-foreground">
          {errorMessage ? t(errorMessage) : '—'}
        </span>
      ) : (
        <span className="relative block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
          {value ?? '—'}
          {children}
        </span>
      )}
    </div>
  );

  if (tooltipContent || (errorMessage && !isEmpty)) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        {(errorMessage && !isEmpty) || tooltipContent ? (
          <TooltipContent>
            {errorMessage && !isEmpty ? (
              <p>{t(errorMessage)}</p>
            ) : (
              tooltipContent
            )}
          </TooltipContent>
        ) : null}
      </Tooltip>
    );
  }

  return content;
};
